import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCurrentStore, updateCurrentStore } from '@/lib/api/stores.js';
import { getDisplayConnectionStatus } from '@/lib/api/display-staff.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { StorePrintingSettings } from '@/pages/settings/PrintingSettingsSection.js';
import { InventoryLabelSettingsSection } from '@/pages/settings/InventoryLabelSettingsSection.js';
import { nullableText } from '@/pages/settings/settings-utils.js';

type DisplayRequired = {
  phone: boolean;
  lastName: boolean;
  email: boolean;
  address: boolean;
  cityStateZip: boolean;
};

const DEFAULT_DISP_REQ: DisplayRequired = {
  phone: true,
  lastName: false,
  email: false,
  address: false,
  cityStateZip: false,
};

export function PrintingSettingsPage() {
  const queryClient = useQueryClient();
  const storeQuery = useQuery({ queryKey: ['store-current'], queryFn: getCurrentStore });
  const store = storeQuery.data;
  const [idleMessage, setIdleMessage] = useState('');
  const [dispReq, setDispReq] = useState<DisplayRequired>(DEFAULT_DISP_REQ);
  const [showTaxLine, setShowTaxLine] = useState(true);
  const [showDevicePin, setShowDevicePin] = useState(false);
  const [showDeviceCondition, setShowDeviceCondition] = useState(false);
  const [queueEnabled, setQueueEnabled] = useState(false);
  const [queueNameMode, setQueueNameMode] = useState<string>('first_name');
  const [queueShowDevice, setQueueShowDevice] = useState(false);
  const [queueShowReason, setQueueShowReason] = useState(true);
  const [queueAbandonMin, setQueueAbandonMin] = useState(45);
  const [queueServiceMin, setQueueServiceMin] = useState(10);
  const [queueAudio, setQueueAudio] = useState(false);

  const displayStatusQuery = useQuery({
    queryKey: ['display-status', store?.id],
    queryFn: () => getDisplayConnectionStatus(store!.id),
    enabled: Boolean(store?.id),
    refetchInterval: 15_000,
  });

  useEffect(() => {
    if (!store) return;
    setIdleMessage(store.displayIdleMessage ?? "We'll be right with you!");
    const saved = store.displayRequiredFields ?? {};
    setDispReq({
      phone: saved.phone !== false,
      lastName: Boolean(saved.lastName),
      email: Boolean(saved.email),
      address: Boolean(saved.address),
      cityStateZip: Boolean(saved.cityStateZip),
    });
    setShowTaxLine(store.displayShowTaxLine !== false);
    setShowDevicePin(Boolean(store.displayShowDevicePin));
    setShowDeviceCondition(Boolean(store.displayShowDeviceCondition));
    setQueueEnabled(Boolean(store.queueEnabled));
    setQueueNameMode(store.queueDisplayNameMode ?? 'first_name');
    setQueueShowDevice(Boolean(store.queueDisplayShowDevice));
    setQueueShowReason(store.queueDisplayShowReason !== false);
    setQueueAbandonMin(store.queueAutoAbandonMinutes ?? 45);
    setQueueServiceMin(store.queueDefaultServiceMinutes ?? 10);
    setQueueAudio(Boolean(store.queueAudioAlert));
  }, [store]);

  const displaySaveMutation = useMutation({
    mutationFn: () =>
      updateCurrentStore({
        displayIdleMessage: nullableText(idleMessage),
        displayRequiredFields: dispReq,
        displayShowTaxLine: showTaxLine,
        displayShowDevicePin: showDevicePin,
        displayShowDeviceCondition: showDeviceCondition,
        queueEnabled,
        queueDisplayNameMode: queueNameMode as
          | 'first_name'
          | 'first_last_init'
          | 'initials'
          | 'number'
          | 'full_name',
        queueDisplayShowDevice: queueShowDevice,
        queueDisplayShowReason: queueShowReason,
        queueAutoAbandonMinutes: queueAbandonMin,
        queueDefaultServiceMinutes: queueServiceMin,
        queueAudioAlert: queueAudio,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-current'] });
      toastSuccess('Display settings saved');
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  const displayUrl = store?.id ? `${window.location.origin}/display/${store.id}` : '';
  const queueDisplayUrl = store?.id
    ? `${window.location.origin}/queue-display/${store.id}`
    : '';

  function copyUrl(url: string, label: string) {
    void navigator.clipboard.writeText(url).then(
      () => toastSuccess(`${label} URL copied`),
      () => toastError('Could not copy URL'),
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">PrintNode Configuration</h2>
        <p className="mt-1 text-sm text-slate-500">PrintNode API Key: Configured in server .env ✓</p>
        <StorePrintingSettings />
      </section>

      <InventoryLabelSettingsSection />

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Display screens</h2>
        <p className="mt-1 text-sm text-slate-600">
          Two separate public screens for the same store. Open each URL on its device — no login
          required. They never interfere with each other.
        </p>
        {store?.id ? (
          <div className="mt-5 space-y-4">
            <div>
              <p className="text-sm font-semibold text-slate-800">Counter customer display</p>
              <p className="text-xs text-slate-500">
                Small screen at one counter — cart, intake, waivers, checkout for one customer.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  readOnly
                  className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-sm"
                  value={displayUrl}
                />
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => copyUrl(displayUrl, 'Customer display')}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => window.open(displayUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Status:{' '}
                {displayStatusQuery.data?.connected
                  ? `Connected${displayStatusQuery.data.connectedAt ? ` since ${new Date(displayStatusQuery.data.connectedAt).toLocaleTimeString()}` : ''}`
                  : 'Not connected'}
              </p>
            </div>
            <div className="border-t border-slate-100 pt-4">
              <p className="text-sm font-semibold text-slate-800">Waiting-area queue TV</p>
              <p className="text-xs text-slate-500">
                Large TV for the whole room — only the walk-in queue. Independent of the counter
                screen; ticket work never blanks this board.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <input
                  readOnly
                  className="min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-sm"
                  value={queueDisplayUrl}
                />
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => copyUrl(queueDisplayUrl, 'Queue display')}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                  onClick={() => window.open(queueDisplayUrl, '_blank', 'noopener,noreferrer')}
                >
                  Open
                </button>
              </div>
              {!queueEnabled ? (
                <p className="mt-2 text-xs font-medium text-amber-800">
                  Enable walk-in queue below so this TV shows the live board.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-bold text-slate-950">Customer Display</h2>
        <p className="mt-1 text-sm text-slate-600">
          Counter iPad options — cart, intake forms, passcode visibility, and idle text.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
          {displayStatusQuery.data?.connected ? (
            <span className="inline-flex items-center gap-2 font-semibold text-emerald-700">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              Display connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 font-semibold text-rose-700">
              <span className="h-2 w-2 rounded-full bg-rose-500" />
              Not connected
            </span>
          )}
          <button
            type="button"
            className="rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
            onClick={() => void displayStatusQuery.refetch()}
          >
            Refresh Status
          </button>
        </div>

        <label className="mt-6 block">
          <span className="text-sm font-medium text-slate-700">Idle Screen Message</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-primary"
            value={idleMessage}
            onChange={(e) => setIdleMessage(e.target.value)}
          />
        </label>

        <div className="mt-6 space-y-2">
          <p className="text-sm font-semibold text-slate-800">Required customer info fields</p>
          <label className="flex items-center gap-2 text-sm text-slate-500">
            <input type="checkbox" checked disabled className="rounded border-slate-300" />
            First Name (required)
          </label>
          {(
            [
              ['phone', 'Phone Number'],
              ['lastName', 'Last Name'],
              ['email', 'Email Address'],
              ['address', 'Street Address'],
              ['cityStateZip', 'City / State / ZIP'],
            ] as const
          ).map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="rounded border-slate-300"
                checked={dispReq[key]}
                onChange={(e) => setDispReq((d) => ({ ...d, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>

        <div className="mt-6 space-y-3 border-t border-slate-100 pt-5">
          <p className="text-sm font-semibold text-slate-800">Totals &amp; device privacy</p>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300"
              checked={showTaxLine}
              onChange={(e) => setShowTaxLine(e.target.checked)}
            />
            <span>
              Show tax line on customer display
              <span className="mt-0.5 block text-xs font-normal text-slate-500">
                When off, the tax row is hidden entirely (including $0). A $0 tax line is always
                suppressed even when this is on.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300"
              checked={showDeviceCondition}
              onChange={(e) => setShowDeviceCondition(e.target.checked)}
            />
            <span>Show device condition under device name</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-slate-300"
              checked={showDevicePin}
              onChange={(e) => setShowDevicePin(e.target.checked)}
            />
            <span>
              Show device passcode / PIN on customer display
              <span className="mt-0.5 block text-xs font-normal text-amber-800">
                ⚠ The customer display faces the shop floor and may be visible to other customers.
                Leave this off unless needed. The passcode is masked by default and revealed on tap.
              </span>
            </span>
          </label>
        </div>

        <div className="mt-8 border-t border-slate-100 pt-6">
          <h3 className="text-base font-bold text-slate-950">Walk-in queue display</h3>
          <p className="mt-1 text-sm text-slate-500">
            Lobby board on the customer display. Disabled by default — hide nav and display board
            when off.
          </p>
          <div className="mt-4 space-y-3">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={queueEnabled}
                onChange={(e) => setQueueEnabled(e.target.checked)}
              />
              Enable walk-in queue
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-700">Name privacy on public display</span>
              <select
                className="mt-1 w-full max-w-md rounded-xl border border-slate-300 px-3 py-2"
                value={queueNameMode}
                onChange={(e) => setQueueNameMode(e.target.value)}
                disabled={!queueEnabled}
              >
                <option value="first_name">First name only (Jonathan)</option>
                <option value="first_last_init">First + last initial (Jonathan S.)</option>
                <option value="initials">Initials (J.S.)</option>
                <option value="number">Queue number (#47)</option>
                <option value="full_name">Full name (Jonathan Smith)</option>
              </select>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-1"
                checked={queueShowDevice}
                disabled={!queueEnabled}
                onChange={(e) => setQueueShowDevice(e.target.checked)}
              />
              <span>
                Show device on public display
                <span className="mt-0.5 block text-xs text-amber-800">
                  Off by default — “iPhone 14 Pro” advertises what someone owns to the room.
                </span>
              </span>
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={queueShowReason}
                disabled={!queueEnabled}
                onChange={(e) => setQueueShowReason(e.target.checked)}
              />
              Show visit reason on public display
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={queueAudio}
                disabled={!queueEnabled}
                onChange={(e) => setQueueAudio(e.target.checked)}
              />
              Audio cue when a name is called (customer display)
            </label>
            <div className="grid max-w-md grid-cols-2 gap-3">
              <label className="text-sm">
                Auto-abandon (min)
                <input
                  type="number"
                  min={5}
                  max={480}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={queueAbandonMin}
                  disabled={!queueEnabled}
                  onChange={(e) => setQueueAbandonMin(Number(e.target.value) || 45)}
                />
              </label>
              <label className="text-sm">
                Default service (min)
                <input
                  type="number"
                  min={1}
                  max={180}
                  className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                  value={queueServiceMin}
                  disabled={!queueEnabled}
                  onChange={(e) => setQueueServiceMin(Number(e.target.value) || 10)}
                />
              </label>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="mt-6 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          disabled={displaySaveMutation.isPending}
          onClick={() => displaySaveMutation.mutate()}
        >
          {displaySaveMutation.isPending ? 'Saving…' : 'Save Display Settings'}
        </button>
      </section>
    </div>
  );
}
